import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Formik, Form } from 'formik';
import yup from 'utils/yup';
import PasswordRequirements from './PasswordRequirements';
import { includeBasePath } from 'utils/url-utils';
import { ODSAlert, ODSButton, ODSGroup } from 'bc-ods';

const messages = defineMessages({
  resetButton: {
    id: 'ResetForm.resetButton',
    defaultMessage: 'Reset password',
  },
  goBackButton: {
    id: 'ResetForm.goBackButton',
    defaultMessage: 'Return to sign in',
  },
  unknownError: {
    id: 'ResetForm.unknownError',
    defaultMessage: 'An unknown error occurred while processing your request. Please try again.',
  },
  buttonBack: {
    id: 'ResetForm.buttonBack',
    defaultMessage: 'Back',
  },
  resetFeedback: {
    id: 'ResetForm.resetFeedback',
    defaultMessage: `{error, select,
      success {Password successfully reset.}
      code {The reset link is invalid.}
      expired {The reset link is expired.}
      used {This password has already been used, pick a new password.}
      min_lifetime {The password was changed too recently, try again later.}
      other {Unknown error}}`,
  },
});

const INITIAL_VALUES = { password: '', confirmPassword: '' };

function useHandleSubmit(resetPassword, setMessage, setError) {
  const handleSubmit = useCallback(
    async (values) => {
      const { error, redirect } = await resetPassword(values.password);
      if (!error) {
        setTimeout(() => {
          window.location.href = redirect ?? includeBasePath('/');
        }, 1000);
        setMessage('success');
        return;
      }

      setError(error);
    },
    [resetPassword, setError, setMessage]
  );

  return handleSubmit;
}

function ResetForm({ resetPassword }: any): ReactElement {
  const [message, setMessage] = useState();
  const [error, setError] = useState();
  const resetFieldValidationSchema = yup.object().shape({
    password: yup
      .string()
      .required('required')
      .min(15, 'min')
      .minLowercase(1, 'lowercase')
      .minUppercase(1, 'uppercase')
      .minNumbers(1, 'digit')
      .minSymbols(1, 'symbol'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), ''], 'match')
      .required(),
  });

  const handleSubmit = useHandleSubmit(resetPassword, setMessage, setError);

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      onSubmit={handleSubmit}
      className="w-100"
      validationSchema={resetFieldValidationSchema}
      validateOnChange={true}
    >
      {({ isSubmitting }) => {
        return (
          <div>
            {message ? (
              <ODSGroup stacked spacing="lg">
                <FormattedMessage {...messages.resetFeedback} values={{ error: message }} />
                <ODSButton
                  size="md"
                  color="primary"
                  className="w-100"
                  link="/login"
                  data-testid="cancel-forgot"
                >
                  <FormattedMessage {...messages.goBackButton} />
                </ODSButton>
              </ODSGroup>
            ) : (
              <Form>
                {error && (
                  <ODSAlert color="danger">
                    <FormattedMessage {...messages.resetFeedback} values={{ error }} />
                  </ODSAlert>
                )}
                <PasswordRequirements size="md" className="mb-3" reset />
                <ODSGroup>
                  <ODSButton
                    className="flex-shrink-0"
                    quiet
                    size="md"
                    link="/login"
                    data-testid="cancel-forgot"
                  >
                    <FormattedMessage {...messages.buttonBack} />
                  </ODSButton>
                  <ODSButton
                    color="primary"
                    className="flex-grow-1"
                    disabled={isSubmitting}
                    type="submit"
                    data-testid="submit"
                    size="md"
                  >
                    <FormattedMessage {...messages.resetButton} />
                  </ODSButton>
                </ODSGroup>
              </Form>
            )}
          </div>
        );
      }}
    </Formik>
  );
}

export default ResetForm;
